import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { MedicamentConsumerRenderProps, withMedicamentContext } from 'components/medicaments/MedicamentsContext';
import WSIndexAddView from 'components/medicaments/WSIndexAddView';
import SearchResultComponent from 'components/SearchResultComponent';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import { FormState } from 'final-form';
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Message, Modal, Segment } from 'semantic-ui-react';
import { getAllTariffCodes, searchMedicaments, updateWsIndexMedicaments } from 'services/medicamentServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { MedicamentDto, MedicamentEnumDto, MedicamentSearchRequest, UpdateWsIndexRequest } from 'ts-types/api.types';
import { numberFormatter } from 'utils/fomattingUtils';
import { noop } from 'utils/functionUtils';
import { required } from 'utils/validatorUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row.totalRow {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;


const StyledGrid = styled(Grid)`
  flex: 1 0 200px;
`;

const StyledMedicament = styled(Segment)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const StyledMedicamentsTableContainer = styled.div`
  height: 97%;
`;

const ButtonsDivider = styled.div`
  margin-top: 5rem;
`;

const TitlesDivider = styled.div`
  margin-bottom: 1rem;
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

interface MedicamentUpdateSearchRequest {
  query: string;
  tariffCode: string;
  fromDate: string,
  showNotSelected: boolean;
  overwriteValue: boolean;
}

interface MedicamentsFieldProps extends FormState {
  values: Partial<MedicamentUpdateSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, MedicamentConsumerRenderProps, NotificationSystemProps {
}

const MEDICAMENT_UPDATE_LIMIT: number = 5000;

interface State {
  foundMedicaments: Array<MedicamentDto>,
  medicamentsLoaded: boolean,
  updateFinished: boolean,
  showNotSelected: boolean,
  tariffCodes: Array<MedicamentEnumDto>,
  selectedTariffCode?: string | undefined,
  addIndexModalOpen: boolean,
  searchValues?: Partial<MedicamentUpdateSearchRequest>
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string,
  selectedMedicamentsWarningMessage?: string
}

class MedicamentsUpdateView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundMedicaments: [],
      medicamentsLoaded: false,
      updateFinished: true,
      showNotSelected: false,
      tariffCodes: [],
      selectedTariffCode: undefined,
      addIndexModalOpen: false,
      cancelTokenSource
    };

    getAllTariffCodes(cancelTokenSource)
        .then(response => {
          this.setState({
            tariffCodes: response
          })
        })
        .catch(this.handleError);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  showModal = () => {
    this.setState({
      addIndexModalOpen: true
    });
  };

  hideModal = (importing?: boolean) => {
    const {searchValues} = this.state;
    if (importing && searchValues) {
      this.medicamentSearch(searchValues);
    }

    this.setState({
      addIndexModalOpen: false
    });
  };

  initialFormValues = () => {
    const {options: {workDate}} = this.props;
    return {
      tariffCode: 402,
      fromDate: workDate,
      showNotSelected: false
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  }

  setWarningMessage = (warningMessage?: string) => {
    this.setState({
      selectedMedicamentsWarningMessage: warningMessage
    });
  }

  handleError = () => {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage("error.general"));
  }

  updateMedicaments = (values: any, selected: boolean) => {

    const {
      selectionNotificationSystem,
      currentUser,
      getMessage,
      getMessageWithNamedParams
    } = this.props;

    const {
      foundMedicaments,
      selectedTariffCode,
      cancelTokenSource
    } = this.state;

    this.setState({
      updateFinished: false
    });

    const medicamentIds: Array<number> = selected
        ? foundMedicaments
            .filter(m => m.selectedForUpdate)
            .map(m => m.id)
        : foundMedicaments.map(m => m.id);

    const selectedMoreThanLimitedNumber = medicamentIds.length > MEDICAMENT_UPDATE_LIMIT;

    const request: UpdateWsIndexRequest = {
      mandateId: currentUser!.userConfig.mandateId,
      fromDate: values.fromDate,
      tariffCode: selectedTariffCode!,
      overwriteValue: values.overwriteValue,
      medicamentIds: medicamentIds
    }

    const onFinally = () => {
      this.setState({
        updateFinished: true
      })
    };

    updateWsIndexMedicaments(request, selected, cancelTokenSource)
        .then(response => {
          //@ts-ignore
          const notification = selectionNotificationSystem.current;
          if (notification) {
            notification.addNotification({
              title: getMessage("medicament.update.success"),
              message: getMessage("medicamentUpdate.successfulUpdate"),
              level: 'success'
            });
          }

          if (selectedMoreThanLimitedNumber) {
            const updatedMedicaments = _.keyBy(response.medicaments, "id");
            const medicaments = Array.from(foundMedicaments);

            medicaments.forEach(med => {
              if (updatedMedicaments[med.id]) {
                med.selectedForUpdate = false
              }
            });

            this.setState({
              foundMedicaments: medicaments
            })

            this.setWarningMessage(
                getMessageWithNamedParams(
                    "medicamentUpdate.selectedMoreThanLimitedNumber.warningMessage",
                    {
                      selectedMedicaments: medicamentIds.length,
                      allowedNumberOfMedicaments: MEDICAMENT_UPDATE_LIMIT
                    }));
          }
        })
        .catch(this.handleError)
        .finally(onFinally);
  };

  medicamentSearch = (values: Partial<MedicamentUpdateSearchRequest>) => {

    const {cancelTokenSource} = this.state;

    this.setState({
      searchValues: values,
      medicamentsLoaded: false
    });

    const onFinally = () => {
      this.setState({
        medicamentsLoaded: true
      })
    };

    const request: MedicamentSearchRequest = {
      query: values.query || "",
      tariffCode: values.tariffCode || "",
      selectedOnly: !values.showNotSelected
    }

    searchMedicaments(request, cancelTokenSource)
        .then(response => {
          let foundMedicaments = response;

          this.setState({
            foundMedicaments: foundMedicaments,
            selectedTariffCode: values.tariffCode
          });
        })
        .catch(noop)
        .finally(onFinally);
  }

  handleChange = debounce(({values}: MedicamentsFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    this.medicamentSearch(values);
  }, 300);

  render(): React.ReactNode {
    return (
        <React.Fragment>
          {this.renderSearchMedicamentSegment()}
          {this.renderWSIndexAddModal()}
        </React.Fragment>
    );
  }

  renderSearchMedicamentSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialFormValues()}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const {getMessage, getMessageWithNamedParams} = this.props;

    const {
      tariffCodes,
      medicamentsLoaded,
      updateFinished,
      errorMessage,
      selectedMedicamentsWarningMessage,
      foundMedicaments
    } = this.state;

    const numResults = foundMedicaments.length;

    const tariffCodeOptions = tariffCodes ? tariffCodes.map(tariffCode => {

      return {
        key: parseInt(tariffCode.code),
        text: `${tariffCode.code}, ${getMessage(`tc.${tariffCode.name}`)}`,
        value: parseInt(tariffCode.code)
      }
    }) : [{key: 400, value: 400, text: `${getMessage(`tc.medicine`)}`}];

    const loaderMessage = !medicamentsLoaded
        ? getMessage("medicament.loading")
        : !updateFinished ? getMessage("medicamentUpdate.updating") : "";

    const selectedMedicaments = foundMedicaments
        .filter(m => m.selectedForUpdate)
        .length;

    return (
        <StyledMedicament>
          <div>
            <PraxFormGrid>
              {errorMessage
                  && (
                      <Grid.Row>
                        <Grid.Column>
                          <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                            {errorMessage}
                          </StyledErrorMessage>
                        </Grid.Column>
                      </Grid.Row>
                  )
              }
              {selectedMedicamentsWarningMessage
                  && (
                      <Grid.Row>
                        <Grid.Column>
                          <Message warning onDismiss={() => this.setWarningMessage()}>
                            {selectedMedicamentsWarningMessage}
                          </Message>
                        </Grid.Column>
                      </Grid.Row>
                  )
              }
              <Grid.Row>
                <Grid.Column width={1} verticalAlign="middle">
                  {getMessage("search")}:
                </Grid.Column>
                <Grid.Column width={7}>
                  <Field
                      name="query"
                      component={Input}
                      fluid
                      autoFocus
                  />
                </Grid.Column>
                <Grid.Column width={1} verticalAlign="middle">
                  {getMessage("tariff")}:
                </Grid.Column>
                <Grid.Column width={7}>
                  <Field
                      name="tariffCode"
                      component={Select}
                      fluid
                      options={tariffCodeOptions}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={16} textAlign="right">
                  <Field
                      name="showNotSelected"
                      component={CheckBox}
                      label={getMessage("medicamentUpdate.including")}
                  />
                </Grid.Column>
              </Grid.Row>
              <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
              <Grid.Row>
                <Grid.Column>
                  {getMessageWithNamedParams("medicamentUpdate.selectedMedicaments",
                      {selectedMedicaments: selectedMedicaments, allMedicaments: foundMedicaments.length})}
                </Grid.Column>
              </Grid.Row>
              <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
            </PraxFormGrid>
          </div>
          <TitlesDivider/>
          <StyledGrid>
            <Grid.Row>
              <Grid.Column width={16}>
                <StyledMedicamentsTableContainer>
                  {!medicamentsLoaded || !updateFinished
                      ? this.renderMedicamentLoader(loaderMessage)
                      : this.renderMedicamentsSegment()}
                </StyledMedicamentsTableContainer>
              </Grid.Column>
            </Grid.Row>
          </StyledGrid>
          <PraxFormGrid>
            <Grid.Row>
              <Grid.Column width={2} verticalAlign="middle">
                {getMessage("from")} {getMessage("date")}:
              </Grid.Column>
              <Grid.Column width={2}>
                <Field
                    name="fromDate"
                    component={DatePicker}
                    validate={required}
                    fluid
                />
              </Grid.Column>
              <Grid.Column width={12}>
                <Field
                    name="overwriteValue"
                    component={CheckBox}
                    label={getMessage("medicamentUpdate.overwriteValue")}
                />
              </Grid.Column>
            </Grid.Row>
          </PraxFormGrid>
          <ButtonsDivider>
            <Button compact type="button" onClick={() => this.updateMedicaments(values, false)} primary>
              {getMessage("medicamentUpdate.wsIndexUpdate")}
            </Button>
            <Button
                compact
                type="button"
                onClick={() => this.updateMedicaments(values, true)}
                primary
                disabled={selectedMedicaments == 0}
            >
              {getMessage("medicamentUpdate.wsIndexUpdateSelected")}
            </Button>
            <Button compact type="button" onClick={() => this.showModal()} primary>
              {getMessage("medicamentUpdate.addIndex")}
            </Button>
          </ButtonsDivider>
        </StyledMedicament>
    )
  };

  selectMedicament = (index: number) => {
    const {foundMedicaments} = this.state;

    const medicament = foundMedicaments[index];
    if (medicament) {
      let medicaments = [...foundMedicaments];
      medicaments[index].selectedForUpdate = !medicaments[index].selectedForUpdate;

      this.setState({
        foundMedicaments: medicaments
      })
    }
  };

  foundMedicamentsRowGetter = ({index}: any) => {
    const {foundMedicaments} = this.state;
    return foundMedicaments[index];
  };

  foundMedicamentsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundMedicaments} = this.state;

    if (!foundMedicaments[index].selection) {
      style.color = '#c6c6cb';
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.selectMedicament(index)}
        >
          {columns}
        </div>
    );
  };

  effectiveDateCellRenderer = (data: any) => {
    if (data.rowData.effectiveDate === null) {
      return <div></div>;
    } else {
      return <div>{moment(data.rowData.effectiveDate).format("DD.MM.YYYY")}</div>;
    }
  }

  priceCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return (
          <StyledCell>{numberFormatter.format(cellData)}</StyledCell>
      );
    }

    return (
        <div></div>
    );
  }

  selectHeaderColumn = () => {

    const {foundMedicaments} = this.state;

    const countSelectableMedicaments = foundMedicaments.length;

    const countSelectedMedicaments =
        foundMedicaments.filter(m => m.selectedForUpdate).length;

    const indeterminate = countSelectedMedicaments > 0
        && countSelectedMedicaments < countSelectableMedicaments;

    const noSelectedMedicaments = countSelectedMedicaments === 0
        && countSelectableMedicaments > 0;

    const allMedicamentsSelected = countSelectableMedicaments > 0
        && countSelectedMedicaments === countSelectableMedicaments;

    return (
        <CheckboxSc
            checked={countSelectedMedicaments > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedMedicaments || indeterminate) {
                  this.changeSelectedForUpdate(true);
                }

                if (allMedicamentsSelected) {
                  this.changeSelectedForUpdate(false);
                }
              }
            }
            disabled={countSelectableMedicaments === 0}
        />
    )
  };

  changeSelectedForUpdate = (isMedicamentSelected: boolean) => {
    const {foundMedicaments} = this.state;

    let medicaments = [...foundMedicaments];
    const selectedMedicaments = medicaments.map(medicament => (
        {...medicament, selectedForUpdate: isMedicamentSelected})
    );

    this.setState({
      foundMedicaments: selectedMedicaments
    });
  }

  medicamentSelectCellRenderer = (data: any) => {
    const {rowIndex, cellData} = data;

    return (
        <CheckboxSc
            checked={cellData}
            onChange={() => this.selectMedicament(rowIndex)}
        />
    )
  };

  renderMedicamentsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundMedicaments} = this.state;

    return (
        <StyledTable
            rowCount={foundMedicaments.length}
            rowGetter={this.foundMedicamentsRowGetter}
            rowRenderer={this.foundMedicamentsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 53,
                flexGrow: 0,
                label: this.selectHeaderColumn(),
                dataKey: "selectedForUpdate",
                cellRenderer: this.medicamentSelectCellRenderer
              },
              {
                width: 103,
                flexGrow: 0,
                label: (getMessage("code")),
                dataKey: "code"
              },
              {
                width: 130,
                flexGrow: 0,
                label: (getMessage("eanCode")),
                dataKey: "eanCode"
              },
              {
                width: 103,
                flexGrow: 0,
                label: (getMessage("pharmaCode")),
                dataKey: "pharmaCode"
              },
              {
                width: 255,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 103,
                flexGrow: 0,
                label: (getMessage("price")),
                dataKey: "billingPrice",
                cellRenderer: this.priceCellRenderer
              },
              {
                width: 110,
                flexGrow: 0,
                label: (getMessage("doctorsPrice")),
                dataKey: "doctorsPrice",
                cellRenderer: this.priceCellRenderer
              },
              {
                width: 103,
                flexGrow: 0,
                label: `${getMessage("from")} ${getMessage("date")}`,
                dataKey: "effectiveDate",
                cellRenderer: this.effectiveDateCellRenderer
              },
              {
                width: 110,
                flexGrow: 0,
                label: (getMessage("oldPrice")),
                dataKey: "oldPrice",
                cellRenderer: this.priceCellRenderer
              }
            ]}
        />
    );
  };

  renderMedicamentLoader = (message: string) => {
    return (
        <LoaderComponent message={message}/>
    )
  }

  renderWSIndexAddModal = () => {

    const {getMessage} = this.props;
    const {addIndexModalOpen} = this.state;

    return (
        <Modal
            open={addIndexModalOpen}
            onClose={() => this.hideModal(false)}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage('medicamentUpdate.addIndex')}</Modal.Header>
          <FormModalContentContainer>
            <WSIndexAddView
                onClose={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }

}

const MedicamentsUpdateViewWrapper =
    withMessages(
        withAuthContext(
            withMedicamentContext(
                withNotificationSystemContext(
                    MedicamentsUpdateView))));


export default MedicamentsUpdateViewWrapper;
